import {noop} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {
  DisplayLayerOptionSection,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapSummary from 'samples/plate-maps/PlateMapSummary';
import {ScratchPlateDesignState} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {useScratchPlateData} from './ScratchPlateDesignSummaryUtil';

export interface ScratchPlateDesignSummaryProps {
  scratchPlateId: string;
  locked: boolean;
  viewer: boolean;
  onEdit: (initialState: ScratchPlateDesignState, scratchPlateId: string, refetch: () => void) => void;
}

const useStyles = createStyles(() => ({
  errorBannerWrapper: {
    alignSelf: 'flex-start',
  },
}));

export default function ScratchPlateDesignSummary({
  scratchPlateId,
  locked,
  viewer,
  onEdit,
}: ScratchPlateDesignSummaryProps): JSX.Element {
  const {classes} = useStyles();
  const {plate, plateMap, containerSchemaId, dataFrameIsInaccessible, refetch} =
    useScratchPlateData(scratchPlateId);

  // Only show schema field layers that have at least one well with a non-null value
  const wellSchemaLayers = useWellSchemaLayers(containerSchemaId, noop, []);
  const wellSchemaOptions = useMemo((): Array<SchematizedDisplayLayerMetadata> => {
    if (wellSchemaLayers == null || plateMap == null) {
      return [];
    }
    const populatedFieldIds = new Set(
      plateMap.wells.flatMap((well) =>
        Object.entries(well.schemaTags)
          .filter(([, tag]) => {
            const v =
              tag.floatValue ??
              tag.integerValue ??
              tag.booleanValue ??
              tag.value ??
              tag.dateValue ??
              tag.datetimeValue ??
              tag.schemaFieldSelectorOptionIds;
            return v != null;
          })
          .map(([apiId]) => apiId)
      )
    );
    return wellSchemaLayers.filter((layer) => populatedFieldIds.has(layer.attribute.key));
  }, [wellSchemaLayers, plateMap]);

  const headerControls = !viewer ? (
    <Button
      size={ButtonSize.MINI}
      icon="pen"
      aria-label="Edit plate design"
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      disabled={!BenchlingConfig.ENABLE_SCRATCH_PLATES || locked || plate == null || plateMap == null}
      onClick={() => {
        if (plate == null || plateMap == null) {
          return;
        }
        const {name, height, width, plateSchemaId} = plate;
        const plateSchema = plateSchemaId != null ? SampleSchemaStore.getPlateSchema(plateSchemaId) : null;
        onEdit(
          {
            name,
            plateSize: [height, width],
            plateSchema,
            plateMap,
          },
          scratchPlateId,
          refetch
        );
      }}
    />
  ) : null;

  return (
    <PlateMapContainer
      clientStateId={`scratch-plate-${scratchPlateId}`}
      header="Plate design"
      headerControls={headerControls}
      subheader={plate?.name ?? scratchPlateId}
      onHeaderContextMenu={noop}
      plateMapType={PlateMapType.DESIGN}
    >
      {dataFrameIsInaccessible ? (
        <div className={classes.errorBannerWrapper}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="You do not have access to this plate design."
          />
        </div>
      ) : plateMap != null ? (
        <PlateMapSummary
          plateMap={plateMap}
          plateDataParametersOptions={[]}
          wellSchemaOptions={wellSchemaOptions}
          frozenReagentAssignmentGroups={null}
          procedureStudyDesignType={null}
          plateMapType={PlateMapType.DESIGN}
          // Scratch plate designs do not have a content layer
          hiddenSections={[DisplayLayerOptionSection.CONTENTS]}
          additionalMetadataContent={<></>}
        />
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </PlateMapContainer>
  );
}
